import type { ReactNode } from 'react';
/**
 * CONTACT-REVEAL-AUDIT-01 — audited tap-to-reveal control (Lead 360 detail ONLY).
 *
 * Renders the masked phone state and, when the backend reports a linked
 * phone, the reveal flow: a REQUIRED quick-pick context (defaulting to
 * "Follow-up call" — ratified decision 8, no free text in v1), the
 * "Reveal phone" action, the revealed value with a copy affordance, and
 * the 60-second client-side auto-rehide (decision 7 — a re-reveal is a
 * new request, therefore a new audit row).
 *
 * SAFETY / HONESTY:
 *   - The masked state carries ZERO digits (PHONE_MASK + the
 *     availability copy are digit-free by test).
 *   - The plaintext exists only in transient component state after a
 *     successful audited reveal, and is dropped on rehide/unmount. It is
 *     never logged, never cached, never written anywhere.
 *   - "No linked phone yet" is a DESIGNED outcome with its own copy
 *     (decision 2), never an error state.
 *   - The audited-reveal notice is always visible next to the control,
 *     so operators know every reveal is recorded.
 *   - This control reads NOTHING from assignedRole / visibleRoles —
 *     display fields are never authorization (decision 6); the backend
 *     scope gate is the only authority and a 401 renders the denied copy.
 *
 * Testability (repo idiom — react-dom/server, no jsdom): the state
 * machine (`nextStateAfterResult`, `shouldRehide`) and the presentational
 * `RevealBlockView` are pure exports; the default container wires state +
 * the rehide timer around an injected `reveal` function.
 */

import { useEffect, useRef, useState } from 'react';

import DrawerShell from '@/dashboard/ui/DrawerShell';
import { createPortal } from 'react-dom';
import { WorkPanel } from '@/dashboard/ui/MasterDetail';
import FieldGrid from '@/dashboard/ui/FieldGrid';
import { WorkRegion } from '@/dashboard/ui/MasterDetail';

import type { Lead360PhoneAvailability } from '@/dashboard/owner/lead-360-view';

// ─── Constants (exported for tests + lockstep with the backend) ───────

/** Client-side auto-rehide horizon — mirrors the backend rehideSeconds. */
export const REVEAL_REHIDE_MS = 60_000;

/** Ratified default quick-pick token ("Follow-up call"). */
export const DEFAULT_REVEAL_PURPOSE = 'follow_up_call';

/** The v1 quick-pick vocabulary — controlled tokens only, default first.
 *  FRONTDESK-CHECKIN-AND-IDENTITY-V1 (rider R-09) adds the honest
 *  front-desk check-in context (the audit row must say WHY the vault
 *  opened; lockstep with CONTACT_REVEAL_QUICK_PICK_PURPOSES). */
export const REVEAL_PURPOSE_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: 'follow_up_call', label: 'Follow-up call' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'billing', label: 'Billing' },
  { value: 'front_desk_checkin', label: 'Front-desk check-in' },
  { value: 'other', label: 'Other' },
];

/** One purpose option (config-backed on the lead plane;
 *  CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1). `requiresNote` drives
 *  the Other-note gate. */
export interface RevealPurposeOption {
  value: string;
  label: string;
  requiresNote?: boolean;
}

/** The empty placeholder for the no-default purpose flow (the charter:
 *  "start with no selected purpose", "Select reason..."). */
export const PURPOSE_PLACEHOLDER_VALUE = '';
export const PURPOSE_PLACEHOLDER_LABEL = 'Select reason...';
export const PURPOSE_REQUIRED_HINT = 'Select a reason to reveal.';
export const OTHER_NOTE_LABEL = 'Reason note';
export const OTHER_NOTE_REQUIRED_HINT = 'A note is required for Other.';

/** Zero-digit presence mask (never derived from the value — static glyphs). */
export const PHONE_MASK = '•••••••••';

export const AUDIT_NOTICE_COPY = 'Reveals are recorded in the audit log.';
export const REVEAL_BUTTON_COPY = 'Reveal phone';
export const REHIDE_NOTICE_COPY = 'Re-hides in 60s';
export const COPY_BUTTON_COPY = 'Copy';
export const NO_LINKED_PHONE_COPY = 'No linked phone yet';
export const AVAILABILITY_UNKNOWN_COPY = 'Phone availability unknown';
export const DENIED_COPY = 'Not available — outside your clinic scope.';
export const REVEAL_FAILED_COPY = 'Could not reveal right now. Try again.';
export const REVEAL_DISABLED_COPY = 'Reveal is not enabled on this environment yet.';

/** Operator-facing availability line per status (all digit-free). */
export const PHONE_AVAILABILITY_COPY: Readonly<
  Record<Lead360PhoneAvailability, string>
> = {
  available: 'Phone on file',
  no_linked_phone: NO_LINKED_PHONE_COPY,
  unknown: AVAILABILITY_UNKNOWN_COPY,
};

// ─── Pure reveal state machine ────────────────────────────────────────

/** Backend result payload (mirror of LeadContactRevealResult). */
export type RevealOutcomePayload =
  | { outcome: 'revealed'; field: string; value: string; rehideSeconds: number }
  | { outcome: 'no_value'; field: string; reason: string };

export type RevealUiState =
  | { kind: 'masked' }
  | { kind: 'revealing' }
  | { kind: 'revealed'; value: string; revealedAtMs: number }
  | { kind: 'no_value' }
  | { kind: 'denied' }
  | { kind: 'disabled' }
  | { kind: 'failed' };

/** Project a backend result into the next UI state. Pure. */
export function nextStateAfterResult(
  result: RevealOutcomePayload,
  nowMs: number,
): RevealUiState {
  if (result.outcome === 'revealed') {
    return { kind: 'revealed', value: result.value, revealedAtMs: nowMs };
  }
  return { kind: 'no_value' };
}

/**
 * Project a reveal FAILURE into the next UI state. Pure. 401 = the
 * backend scope gate denied (the only authority); 503 = the endpoint is
 * dark (flag off); anything else is a transient failure.
 */
export function nextStateAfterError(status: number | null): RevealUiState {
  if (status === 401) return { kind: 'denied' };
  if (status === 503) return { kind: 'disabled' };
  return { kind: 'failed' };
}

/** True once a revealed value must re-mask (decision 7: 60s horizon). */
export function shouldRehide(revealedAtMs: number, nowMs: number): boolean {
  return nowMs - revealedAtMs >= REVEAL_REHIDE_MS;
}

// ─── Presentational block (pure — renderToString-testable) ────────────

export interface RevealBlockViewProps {
  availability: Lead360PhoneAvailability;
  state: RevealUiState;
  purpose: string;
  onPurposeChange?: (purpose: string) => void;
  onReveal?: () => void;
  onCopy?: () => void;
  /**
   * CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1 (additive; all default
   * to today's behavior so existing callers stay byte-identical):
   *   - purposeOptions: the config-backed purpose vocabulary (defaults to
   *     REVEAL_PURPOSE_OPTIONS);
   *   - requireSelection: render the "Select reason..." placeholder first
   *     and DISABLE Reveal until a valid purpose is chosen (the charter's
   *     no-default-purpose rule);
   *   - noteValue / onNoteChange: the Other-note field (shown + required
   *     when the selected purpose is flagged requiresNote).
   */
  purposeOptions?: ReadonlyArray<RevealPurposeOption>;
  requireSelection?: boolean;
  noteValue?: string;
  onNoteChange?: (note: string) => void;
  /**
   * LEADS-WORKSPACE-ROLE-SCOPED-01 inherited polish, flag-gated by the
   * caller (me.leadsWorkspaceEnabled): adds the `--v2` row layout
   * (flex + gap — fixes the "Phone on file" / "Context:" run-on) and
   * the no-wrap rehide notice. Default FALSE renders today's markup
   * byte-identically.
   */
  polished?: boolean;
  /**
   * FRONTDESK-CHECKIN-AND-IDENTITY-V1 (rider R-09): render the reveal flow
   * even when availability is 'unknown' -- the check-in identity step has no
   * availability signal on its wire, and the ENDPOINT answers honestly
   * either way (the DESIGNED no_value outcome renders its own copy after an
   * attempt). Default FALSE: Lead-360 (which knows availability from its own
   * read) stays byte-identical, and a KNOWN 'no_linked_phone' still renders
   * no dead button.
   */
  allowAttemptWhenUnknown?: boolean;
}

function rowClass(polished: boolean): string {
  return polished
    ? 'lead360-contact__row lead360-contact__row--v2'
    : 'lead360-contact__row';
}

/**
 * The phone row of the Lead 360 contact block. Pure: renders entirely
 * from props. The masked value is the static digit-free PHONE_MASK; the
 * plaintext appears ONLY in the 'revealed' state.
 */
export function RevealBlockView({
  availability,
  state,
  purpose,
  onPurposeChange,
  onReveal,
  onCopy,
  polished = false,
  allowAttemptWhenUnknown = false,
  purposeOptions = REVEAL_PURPOSE_OPTIONS,
  requireSelection = false,
  noteValue = '',
  onNoteChange,
}: RevealBlockViewProps): JSX.Element {
  if (
    availability !== 'available' &&
    !(allowAttemptWhenUnknown && availability === 'unknown')
  ) {
    // Designed non-reveal states: nothing to reveal — no control shown.
    return (
      <div className={rowClass(polished)} data-testid="lead360-phone-row">
        <span className="lead360-contact__mask" data-testid="lead360-phone-mask">
          {PHONE_MASK}
        </span>
        <span
          className="lead360-muted"
          data-testid="lead360-phone-availability"
          data-availability={availability}
        >
          {PHONE_AVAILABILITY_COPY[availability]}
        </span>
      </div>
    );
  }

  if (state.kind === 'revealed') {
    return (
      <div className={rowClass(polished)} data-testid="lead360-phone-row">
        <span
          className="lead360-contact__value"
          data-testid="lead360-phone-revealed"
        >
          {state.value}
        </span>
        <button
          type="button"
          className="ac-btn ac-btn--secondary ac-btn--sm"
          data-testid="lead360-phone-copy"
          onClick={onCopy}
        >
          {COPY_BUTTON_COPY}
        </button>
        <span
          className={
            polished ? 'lead360-muted lead360-nowrap' : 'lead360-muted'
          }
          data-testid="lead360-phone-rehide"
        >
          {REHIDE_NOTICE_COPY}
        </span>
      </div>
    );
  }

  const selectedOption = purposeOptions.find((o) => o.value === purpose) ?? null;
  const noteRequired = selectedOption?.requiresNote === true;
  const noteMissing = noteRequired && noteValue.trim().length === 0;
  // The no-default-purpose gate (the charter): Reveal stays disabled until a
  // valid purpose is chosen AND any required note is present. The SERVER
  // re-validates every reveal -- this is honest UI, never the control.
  const purposeMissing = requireSelection && !selectedOption;
  const revealDisabled =
    state.kind === 'revealing' || purposeMissing || noteMissing;
  return (
    <div className={rowClass(polished)} data-testid="lead360-phone-row">
      <span className="lead360-contact__mask" data-testid="lead360-phone-mask">
        {PHONE_MASK}
      </span>
      <span
        className="lead360-muted"
        data-testid="lead360-phone-availability"
        data-availability={availability}
      >
        {PHONE_AVAILABILITY_COPY[availability]}
      </span>
      <label className="lead360-contact__purpose-label">
        Context:{' '}
        <select
          data-testid="lead360-reveal-purpose"
          value={purpose}
          onChange={(e) => onPurposeChange?.(e.target.value)}
        >
          {requireSelection ? (
            <option value={PURPOSE_PLACEHOLDER_VALUE} disabled>
              {PURPOSE_PLACEHOLDER_LABEL}
            </option>
          ) : null}
          {purposeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      {noteRequired ? (
        <label className="lead360-contact__purpose-label">
          {OTHER_NOTE_LABEL}:{' '}
          <input
            type="text"
            data-testid="lead360-reveal-note"
            value={noteValue}
            maxLength={200}
            onChange={(e) => onNoteChange?.(e.target.value)}
          />
        </label>
      ) : null}
      {/* G5 (ENTERPRISE-CRM-EXPERIENCE-ELEVATION-V1): ONE primary per
          region -- the record's primary is Log outcome; the audited reveal
          is a lawful SECONDARY door (behavior, purpose gating, and audit
          flow unchanged). */}
      <button
        type="button"
        className="ac-btn ac-btn--secondary ac-btn--sm"
        data-testid="lead360-reveal-button"
        disabled={revealDisabled}
        onClick={onReveal}
      >
        {state.kind === 'revealing' ? 'Revealing…' : REVEAL_BUTTON_COPY}
      </button>
      {purposeMissing ? (
        <span className="lead360-muted" data-testid="lead360-reveal-purpose-hint">
          {PURPOSE_REQUIRED_HINT}
        </span>
      ) : null}
      {noteMissing ? (
        <span className="lead360-muted" data-testid="lead360-reveal-note-hint">
          {OTHER_NOTE_REQUIRED_HINT}
        </span>
      ) : null}
      {state.kind === 'no_value' ? (
        <span className="lead360-muted" data-testid="lead360-reveal-no-value">
          {NO_LINKED_PHONE_COPY}
        </span>
      ) : null}
      {state.kind === 'denied' ? (
        <span className="lead360-muted" data-testid="lead360-reveal-denied">
          {DENIED_COPY}
        </span>
      ) : null}
      {state.kind === 'disabled' ? (
        <span className="lead360-muted" data-testid="lead360-reveal-disabled">
          {REVEAL_DISABLED_COPY}
        </span>
      ) : null}
      {state.kind === 'failed' ? (
        <span className="lead360-muted" data-testid="lead360-reveal-failed">
          {REVEAL_FAILED_COPY}
        </span>
      ) : null}
    </div>
  );
}

// ─── Container (state + 60s rehide timer around an injected reveal fn) ─

export interface LeadContactRevealControlProps {
  /** Founder-approved Option A: a purposeful action opens the V6 drawer. */
  followupPatientName?: string;
  followupContext?: string;
  followupTriggerLabel?: string;
  followupFooter?: ReactNode;
  followupNotice?: string | null;
  followupBusy?: boolean;
  panelTarget?: HTMLElement | null;
  onPanelChange?: (open: boolean) => void;
  taskDisabled?: boolean;
  availability: Lead360PhoneAvailability;
  /**
   * Performs the audited reveal (POST /api/leads/:id/contact-reveal via
   * `revealLeadContact` in @/dashboard/api). Injected so the pure tests
   * and the detail page can wire it independently. Errors should carry
   * an HTTP `status` when known (PortalApiError does). The optional second
   * arg is the Other-note (CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1)
   * -- omitted for every purpose that does not require one.
   */
  reveal: (purpose: string, note?: string) => Promise<RevealOutcomePayload>;
  /** See RevealBlockViewProps.polished — default FALSE (today's markup). */
  polished?: boolean;
  /** Pre-selected quick-pick purpose (must be a REVEAL_PURPOSE_OPTIONS
   *  value). Default stays 'follow_up_call' (byte-identical Lead-360
   *  behavior); the check-in identity step passes 'front_desk_checkin' so
   *  the audit row records the true desk context (rider R-09). The
   *  operator can still change the pick. IGNORED when requireSelection is
   *  true (the no-default flow starts blank). */
  initialPurpose?: string;
  /** See RevealBlockViewProps.allowAttemptWhenUnknown (rider R-09). Default
   *  FALSE (Lead-360 byte-identical). */
  allowAttemptWhenUnknown?: boolean;
  /** CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1: the config-backed
   *  purpose vocabulary (defaults to REVEAL_PURPOSE_OPTIONS). */
  purposeOptions?: ReadonlyArray<RevealPurposeOption>;
  /** CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1: the no-default
   *  purpose flow ("Select reason...", Reveal disabled until chosen).
   *  Default FALSE (every existing caller byte-identical). */
  requireSelection?: boolean;
}

export default function LeadContactRevealControl({
  followupPatientName,
  followupContext,
  followupTriggerLabel = 'Call for follow-up',
  followupFooter,
  followupNotice,
  followupBusy = false,
  panelTarget,
  onPanelChange,
  taskDisabled = false,
  availability,
  reveal,
  polished = false,
  initialPurpose = DEFAULT_REVEAL_PURPOSE,
  allowAttemptWhenUnknown = false,
  purposeOptions = REVEAL_PURPOSE_OPTIONS,
  requireSelection = false,
}: LeadContactRevealControlProps): JSX.Element {
  const [state, setState] = useState<RevealUiState>({ kind: 'masked' });
  const [taskOpen, setTaskOpen] = useState(false);
  const requestVersion = useRef(0);
  const taskButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => () => { requestVersion.current += 1; }, []);
  const wasTaskOpen = useRef(false);
  useEffect(() => {
    if (wasTaskOpen.current && !taskOpen) taskButtonRef.current?.focus();
    wasTaskOpen.current = taskOpen;
  }, [taskOpen]);
  useEffect(() => () => onPanelChange?.(false), [onPanelChange]);
  const closeTask = (): void => {
    if (followupBusy) return;
    requestVersion.current += 1;
    setState({ kind: 'masked' });
    setTaskOpen(false);
    onPanelChange?.(false);
    taskButtonRef.current?.focus();
  };
  const [purpose, setPurpose] = useState<string>(
    requireSelection ? PURPOSE_PLACEHOLDER_VALUE : initialPurpose,
  );
  const [note, setNote] = useState<string>('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Decision 7: auto-rehide — the plaintext is dropped from state after
  // REVEAL_REHIDE_MS (and on unmount). A re-reveal is a new audit row.
  useEffect(() => {
    if (state.kind !== 'revealed') return undefined;
    timerRef.current = setTimeout(() => {
      setState({ kind: 'masked' });
    }, REVEAL_REHIDE_MS);
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [state]);

  const selectedOption = purposeOptions.find((o) => o.value === purpose) ?? null;
  const onReveal = (): void => {
    // The no-default gate: never fire without a valid purpose, and never
    // without the required note (the SERVER re-validates regardless).
    if (requireSelection && selectedOption === null) return;
    const noteToSend =
      selectedOption?.requiresNote === true ? note.trim() : undefined;
    if (selectedOption?.requiresNote === true && (noteToSend ?? '').length === 0) {
      return;
    }
    const version = ++requestVersion.current;
    setState({ kind: 'revealing' });
    reveal(purpose, noteToSend)
      .then((result) => {
        if (version !== requestVersion.current) return;
        setState(nextStateAfterResult(result, Date.now()));
      })
      .catch((err: unknown) => {
        if (version !== requestVersion.current) return;
        const status =
          err !== null &&
          typeof err === 'object' &&
          'status' in err &&
          typeof (err as { status: unknown }).status === 'number'
            ? (err as { status: number }).status
            : null;
        setState(nextStateAfterError(status));
      });
  };

  const onCopy = (): void => {
    if (state.kind === 'revealed' && navigator.clipboard !== undefined) {
      void navigator.clipboard.writeText(state.value);
    }
  };

  if (followupPatientName !== undefined) {
    const startTask = (): void => {
      setTaskOpen(true);
      onPanelChange?.(true);
      onReveal();
    };
    const feedback = state.kind === 'denied' ? DENIED_COPY
      : state.kind === 'disabled' ? REVEAL_DISABLED_COPY
      : state.kind === 'failed' ? REVEAL_FAILED_COPY
      : state.kind === 'no_value' ? NO_LINKED_PHONE_COPY
      : state.kind === 'revealing' ? 'Loading phone...'
      : 'Phone hidden';
    const Panel = panelTarget ? WorkPanel : DrawerShell;
    const panel = (
          <Panel title="Call for follow-up" onClose={closeTask}
            closeDisabled={followupBusy} testId="followup-contact-drawer">
            <FieldGrid columns={1} fields={[{ label: 'Patient', value: followupPatientName }, ...(followupContext ? [{ label: 'Follow-up', value: followupContext }] : [])]} />
            <WorkRegion eyebrow="Phone">
            {state.kind === 'revealed' ? (
              <RevealBlockView availability={availability} state={state}
                purpose={purpose} onCopy={onCopy} polished
                allowAttemptWhenUnknown={allowAttemptWhenUnknown} />
            ) : (
              <p role="status" data-testid="followup-contact-status">{feedback}</p>
            )}
            {state.kind === 'failed' || state.kind === 'masked' ? (
              <button type="button" className="ac-btn ac-btn--secondary ac-btn--sm"
                onClick={onReveal}>{state.kind === 'failed' ? 'Try again' : 'Show phone again'}</button>
            ) : null}
            </WorkRegion>
            <p className="lead360-muted">Viewing the number does not complete the follow-up.</p>
            <p className="lead360-muted">{AUDIT_NOTICE_COPY}</p>
            {followupNotice ? <p role="alert">{followupNotice}</p> : null}
            {followupFooter}
          </Panel>
    );
    return (
      <div className="lead360-contact" data-testid="lead360-reveal-control" data-panel-open={taskOpen || undefined}>
        <button type="button" className="ac-btn ac-btn--secondary ac-btn--sm"
          disabled={taskDisabled} data-testid="followup-contact-open" ref={taskButtonRef} onClick={startTask}>
          {followupTriggerLabel}
        </button>
        {taskOpen ? (
          panelTarget ? createPortal(panel, panelTarget) : panel
        ) : null}
      </div>
    );
  }

  return (
    <div className="lead360-contact" data-testid="lead360-reveal-control">
      <RevealBlockView
        availability={availability}
        state={state}
        purpose={purpose}
        onPurposeChange={setPurpose}
        onReveal={onReveal}
        onCopy={onCopy}
        polished={polished}
        allowAttemptWhenUnknown={allowAttemptWhenUnknown}
        purposeOptions={purposeOptions}
        requireSelection={requireSelection}
        noteValue={note}
        onNoteChange={setNote}
      />
      <p className="lead360-muted lead360-contact__notice" data-testid="lead360-reveal-notice">
        {AUDIT_NOTICE_COPY}
      </p>
    </div>
  );
}
